import { Component, OnInit, ChangeDetectorRef, AfterViewChecked, OnDestroy, ViewEncapsulation, ViewChild, Input } from '@angular/core';
import
{
  GridDataResult, DataStateChangeEvent, RowClassArgs, CellClickEvent,
  GridComponent, ColumnReorderEvent, ColumnVisibilityChangeEvent
} from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { TaskUtility } from '../task.utility';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { TaskListService } from '../../core/service/grid-data/grid-data-task.service';
import { State } from '@progress/kendo-data-query';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { FilterService } from '../../core/filter/filter.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { EventListService } from 'src/app/core/service/grid-data/grid-data-event.service';
import moment from 'moment';
import { SettingService } from 'src/app/core/service/setting.service';
import { Filter } from 'src/app/core/filter/filter';
import { ChannelService } from 'src/app/core/service/channel.service';
import { IntlService } from '@progress/kendo-angular-intl';
import { TaskListUtils } from 'src/app/core/service/grid-data/grid-data-task-list-utils.service';
import { RowArgs } from '@progress/kendo-angular-grid';
import { ColumnSettings } from 'src/app/core/model/column-settings';
import { cloneDeep } from 'lodash';
import { GridResizeEvent } from 'src/app/core/model/grid-settings';
import { ResizedEvent } from 'src/app/shared/resize-event/resized-event';
import { createKeysetSortStrategy } from 'src/app/core/keyset-paging/keyset-paging-sort';
import { isSimplifiedPagingEnabledForRole } from 'src/app/core/keyset-paging/simplified-paging-role';
import { KeysetPagingStore } from 'src/app/shared/keyset-paging/keyset-paging-store';
@Component({
  standalone: false,
  selector: 'app-task-list',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './task-list.component.html',
  styleUrls: ['./task-list.component.scss']
})
export class TaskListComponent implements OnInit, OnDestroy, AfterViewChecked
{
  @ViewChild(GridComponent) public grid: GridComponent;

  @Input() public dashboard: boolean;

  public autoFitColumnsEnabled = false;
  /**
   * Simplified task grid paging: keyset navigation when {@code pagingRestrictionTaskEnabled}
   * and {@code isSimplifiedPagingEnabledForRole} applies for the current user.
   */
  public isSimplifiedPaging = false;
  public keysetPaging!: KeysetPagingStore;
  public pageSizes = [5, 10, 25, 50, 100];
  public gridView: GridDataResult;
  public gridData: any = {};
  public total: number;
  public communityEnabled = false;
  public expandedDetailKeys: any[] = [];
  public gridContainerWidth = undefined;

  /**
   * Task list Kendo grid state.
   */
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    }
  };

  public daysOnListAxis: any = {
    min: 0,
    max: 30,
    plotBands: [{
      from: 0, to: 10, color: '#e6e6e6'
    }, {
      from: 10, to: 20, color: '#fbe4b6'
    }, {
      from: 20, to: 30, color: '#fab59e'
    }]
  };

  public enableAdvanced = false;
  public enableAutoCollapse = false;
  public currentUser = this.authenticationService.currentUser;
  public breadcrumb = '';
  public stringLiteralBreadcrumb = false;
  public filterDialogOpened = false;
  public assigneeDialogOpened = false;
  public taskDueDateOpened = false;
  public task = null;
  public priorityDialogOpened = false;
  public filterText = '';
  public showDetails = false;
  public distinctPriorities: Array<any> = [];
  public filterSelected: Filter;
  public patientLabel = {};
  public defaultGridState = null;
  public defaultColumnsConfig: ColumnSettings[];
  public columnsConfig: ColumnSettings[];

  /**
   * Subscription to Angular component route parameters.
   */
  public routeParamSubscription: Subscription;

  private messagingSubscription: Subscription;
  private listSubscription: Subscription;

  constructor(
    private filterService: FilterService,
    private authenticationService: AuthenticationService,
    private route: ActivatedRoute,
    private router: Router,
    private taskListService: TaskListService,
    private eventListService: EventListService,
    private changeDetectorRef: ChangeDetectorRef,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService,
    private messengerService: MessengerService,
    private taskUtilityService: TaskUtility,
    private channelService: ChannelService,
    private settingService: SettingService,
    public intl: IntlService)
  {
    this.state.take = this.appConfigService.getConfig().pageSize;
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.taskListService.loading;
  }

  public get taskGridPageable(): boolean | { buttonCount: number; pageSizes: number[] }
  {
    if (this.isSimplifiedPaging)
    {
      return true;
    }
    return this.total > this.state.take ? { buttonCount: 5, pageSizes: this.pageSizes } : false;
  }

  public ngOnInit(): void
  {
    if (this.settingService.reloadIfPagingRestrictionPending('task'))
    {
      return;
    }

    this.keysetPaging = new KeysetPagingStore({
      sortStrategy: createKeysetSortStrategy({
        defaultSort: { sortProperty: 'openedOn', sortDirection: 'DESC' }
      }),
      getSortContext: () => ({ recentOnly: false }),
      getBodyExtra: () => ({}),
      getAnchorExtras: () => ({}),
      getState: () => this.state,
      postKeyset: (body, meta) => this.taskListService.searchGridKeyset(body, meta),
      isEnabled: () => this.isSimplifiedPaging
    });

    this.listSubscription = this.taskListService.subscribe({
      next: (value) => this.applyTaskListGridData(value)
    });

    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message.type === 'taskDueDateCancelled' || message.type === 'taskDueDateChanged')
        {
          this.taskDueDateOpened = false;
        }
      }
    });

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        const pagingTask = sessionSetting.pagingRestrictionTaskEnabled;
        const role = this.currentUser.role;
        this.isSimplifiedPaging = isSimplifiedPagingEnabledForRole(pagingTask, role);
        this.autoFitColumnsEnabled = sessionSetting.autofitGridColumnsTask;
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
        this.communityEnabled = sessionSetting.communityEnabled;
        this.showDetails = false;
        const priorities = sessionSetting.taskPriorities.split(',');
        priorities.forEach(p =>
        {
          this.distinctPriorities.push({ priorityName: p, priority: p.toUpperCase() });
        });

        this.filterService.getFilter(sessionSetting.defaultGridFilterTask).subscribe({
          next: (data: Filter) =>
          {
            this.taskListService.setDefaultColumnsSetting(data?.state ? data.state : '', data?.columns ? data.columns : '',
              this.appConfigService.getConfig().pageSize, this.communityEnabled).then((config: any) =>
              {
                // Keeping default grid state and column config
                this.defaultGridState = cloneDeep(config.state);
                this.defaultColumnsConfig = cloneDeep(config.columns);

                // Current grid state and column config
                this.state = cloneDeep(config.state);
                this.columnsConfig = cloneDeep(config.columns);

                // fetch tasks
                this.routeParamSubscription = this.route.params.subscribe({
                  next: (params) =>
                  {
                    this.filterSelected = undefined;
                    let filter = params.filter;
                    if (this.dashboard)
                    {
                      filter = 'my';
                    }
                    this.getTasks(filter);
                  }
                });
              });
          }
        });
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.routeParamSubscription !== undefined) { this.routeParamSubscription.unsubscribe(); }
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
  }

  public ngAfterViewChecked(): void
  {
    // explicit change detection to avoid "expression-has-changed-after-it-was-checked-error"
    this.changeDetectorRef.detectChanges();
  }

  /**
   * This method is to set the gridContainerWidth variable which is passed to the task details UI when none-modal.
   *
   * @param e ResizedEvent
   */
  public onResized(e: ResizedEvent): void
  {
    this.gridContainerWidth = e.newWidth;
  }

  /**
   * Sets the selected task, and opens the task assignment dialog modal.
   *
   * @param task Selected task for re-assignment.
   */
  public openTaskAssigneeDialog(task): void
  {
    this.task = task;
    this.assigneeDialogOpened = true;
  }

  /**
   * Sets the selected task to null, and closes the task assignment dialog modal.
   */
  public closeTaskAssigneeDialog(): void
  {
    this.task = null;
    this.assigneeDialogOpened = false;
  }

  /**
   * Sets the selected task, and opens the task assignment dialog modal.
   *
   * @param task Selected task for re-assignment.
   */
  public openTaskDueDateDialog(task): void
  {
    this.task = task;
    this.taskDueDateOpened = true;
  }

  /**
   * Sets the selected task to null, and closes the task assignment dialog modal.
   */
  public closeTaskDueDateDialog(): void
  {
    this.task = null;
    this.taskDueDateOpened = false;
  }

  /**
   * Updates a task's assignee.
   *
   * @param assignee Form of the task-assign component.
   */
  public updateTaskAssignee(assignee: any): void
  {
    this.task.assigneeName = assignee.name;
    this.task.assignee = assignee.username;
    this.closeTaskAssigneeDialog();
    this.assigneeUpdated(assignee);
  }

  /**
   * Assignee updated called from assignee update from grid or task details component.
   *
   * @param task Task object.
   */
  public assigneeUpdated(task: any): void
  {
    // If task assignee is updated and no longer current logged in user, just reload
    if (this.breadcrumb === 'MY-OPEN-TASKS' && task.username !== this.currentUser.userName)
    {
      this.getTasks('my');
    }
  }

  public onTaskCompleted(): void
  {
    this.unselectTask();

    // Reset to page 1 if last item of the page has been completed.
    if (this.state.skip > 0 && this.gridView.data.length === 1)
    {
      this.state.skip = 0;
    }

    // refresh the list to remove the completed task and retrieve any new tasks that are created because of it.
    this.queryByState();
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.state.filter = filter;
    this.queryByState();
  }

  public searchFieldChange(filterText: string): void
  {
    this.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  /**
   * Updates a task's due date.
   *
   * @param task Selected task for re-assignment.
   * @param assigneeForm Form of the task-assign component.
   */
  public updateTaskDueDate(task): void
  {
    this.task.dueDate = task.dueDate;
    this.taskDueDateOpened = false;
  }

  public rowCallback(context: RowClassArgs): any
  {
    return {
      low: context.dataItem.priority === 'Low',
      medium: context.dataItem.priority === 'Medium',
      high: context.dataItem.priority === 'High',
      urgent: context.dataItem.priority === 'Urgent'
    };
  }

  public counter(i: number): any
  {
    return new Array(i);
  }

  /**
   * Event fires when a task is selected.
   */
  public onCellClick = (event: CellClickEvent): void =>
  {
    if (this.enableAutoCollapse)
    {
      if (!this.isExpanded(event.dataItem.id))
      {
        this.collapseAllRows();
        this.expandedDetailKeys.push(event.dataItem.id);
      }
      else
      {
        this.collapseAllRows();
      }
    }
    else
    {
      if (event && event.dataItem && event.dataItem.id)
      {
        if (!this.isExpanded(event.dataItem.id))
        {
          this.expandedDetailKeys.push(event.dataItem.id);
        }
        else
        {
          const newKeys = this.expandedDetailKeys.filter(x => x !== event.dataItem.id);
          this.expandedDetailKeys = newKeys;
        }
      }
    }
  };

  public isTaskDetailExpanded = ({ dataItem }: RowArgs): boolean =>
  {
    if (this.expandedDetailKeys !== undefined)
    {
      return this.expandedDetailKeys.includes(dataItem.id);
    }
    else
    {
      return false;
    }
  };

  /**
   * Sets the selected task, and opens the task priority dialog modal.
   *
   * @param task Selected task for re-assignment.
   */
  public openTaskPriorityDialog(task): void
  {
    this.task = task;
    this.priorityDialogOpened = true;
  }

  /**
   * Updates a task's priority.
   *
   * @param task Selected task for re-prioritize.
   * @param assigneeForm Form of the task-assign component.
   */
  public updateTaskPriority(task): void
  {
    this.task.priorityNumber = task.priorityNumber;
    this.task.priority = task.priority;
    this.task.priorityBadgeClass = this.taskUtilityService.priorityBadgeClass(task.priority);
    this.priorityDialogOpened = false;
  }

  public closeTaskPriorityDialog(): void
  {
    this.priorityDialogOpened = false;
  }

  public expandDetailsBy = (dataItem: any): any => dataItem.id;


  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.unselectTask();
    this.state = state;
    this.keysetPaging.onDataStateChangeElse(state, () => this.queryTasks(this.state));
  }

  /**
   * Grid column visibility changed.
   *
   * @param event Grid column visibility event.
   */
  public onVisibilityChange(event: ColumnVisibilityChangeEvent): void
  {
    event.columns.forEach((column: any) =>
    {
      const field = this.columnsConfig.find((col) => col.field === column.field);
      if (field !== undefined)
      {
        field.hidden = column.hidden;
      }
    });
  }

  /**
   * Grid onResize event.
   *
   * @param event Grid on resize column event.
   */
  public onResize(event: Array<GridResizeEvent>): void
  {
    event.forEach(item =>
    {
      const field = this.columnsConfig.find((col) => col.field === item.column.field);
      if (field !== undefined)
      {
        field.width = item.newWidth;
      }
    });
  }

  /**
   * Reorder grid column order event
   *
   * @param e ColumnReorderEvent event
   */
  public onReorder(e: ColumnReorderEvent): void
  {
    const reorderedColumn = this.columnsConfig.splice(e.oldIndex, 1);
    this.columnsConfig.splice(e.newIndex, 0, ...reorderedColumn);
  }

  public pageChange(event: any): void
  {
    const takeChanged = event.take !== this.state.take;
    this.state.take = event.take;
    if (takeChanged && this.isSimplifiedPaging)
    {
      this.keysetPaging.onPageSizeChange();
    }
  }

  /**
   * Reset grid columns with default state
   */
  public resetGridColumns(): void
  {
    this.columnsConfig = cloneDeep(this.defaultColumnsConfig);
    this.state.take = this.defaultGridState.take;     // Reset just the pagesize from default grid state
    this.refresh();
  }

  public refresh(): void
  {
    this.state.skip = 0;
    this.queryByState();
  }

  /**
   * Get cases from API per the applied filter.
   *
   * @param filter Task filter string.
   */
  private getTasks(filter: string): void
  {
    this.unselectTask();
    const channelId = this.route.snapshot.queryParamMap.get('channel');

    // When the task (follow-up) is related with another task
    if (this.router.url.includes('/task/'))
    {
      let taskId = null;
      this.route.params.forEach((params: Params) => { taskId = params.id; });

      this.columnsConfig = cloneDeep(this.defaultColumnsConfig);
      this.state = cloneDeep(this.defaultGridState);
      this.state.filter = {
        filters: [
          { field: 'id', operator: 'eq', value: taskId },
        ], logic: 'and'
      };

      this.taskListService.queryByState(this.state, (task: any) =>
      {
        // Set breadcrumb
        if (task && task.total === 1)
        {
          this.breadcrumb = task.data[0].name;
          this.stringLiteralBreadcrumb = true;
          this.showDetails = true;

          this.expandedDetailKeys.push(task.data[0].id);

          window.scroll(0, 0);
        }
      }, null, '');
    }
    else if (filter === 'active' || filter === 'completed' || filter === 'my')
    {
      this.columnsConfig = cloneDeep(this.defaultColumnsConfig);
      this.state = cloneDeep(this.defaultGridState);

      this.breadcrumb = filter === 'active' ? 'ACTIVE' : filter === 'my' ? 'MY-OPEN-TASKS' : 'COMPLETED';
      this.stringLiteralBreadcrumb = false;

      if (filter === 'active')
      {
        this.state.filter = {
          filters: [
            { field: 'closedOn', operator: 'isnull', value: '' },
          ], logic: 'and'
        };
      }
      else if (filter === 'completed')
      {
        this.state.filter = {
          filters: [
            { field: 'closedOn', operator: 'isnotnull', value: '' },
          ], logic: 'and'
        };
      }
      else if (filter === 'my')
      {
        this.state.filter = {
          filters: [
            { field: 'assigneeName', operator: 'eq', value: this.currentUser.name },
            { field: 'closedOn', operator: 'isnull', value: '' }
          ], logic: 'and'
        };
      }
      else
      {
        delete this.state.filter;
      }

      if (channelId)
      {
        this.channelService.getChannel(Number(channelId)).subscribe({
          next: (channel) =>
          {
            const now = new Date();
            const today = moment(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0));
            const allAssignee = this.route.snapshot.queryParamMap.get('allAssignee');

            // AllAssignee is true when called from the Program List, therefore the assigneeName is filtered only from the Dashboard
            if (allAssignee === 'true')
            {
              this.state.filter = {
                filters: [
                  { field: 'closedOn', operator: 'isnull', value: '' },
                  { field: 'channel', operator: 'eq', value: channel.name },
                  { field: 'dueDate', operator: 'lt', value: today.toDate() },
                ], logic: 'and'
              };
            }
            else
            {
              this.state.filter = {
                filters: [
                  { field: 'closedOn', operator: 'isnull', value: '' },
                  { field: 'channel', operator: 'eq', value: channel.name },
                  { field: 'assigneeName', operator: 'eq', value: this.currentUser.name },
                  { field: 'dueDate', operator: 'lt', value: today.toDate() },
                ], logic: 'and'
              };
            }
            this.queryByState();
          }
        });
      }
      else
      {
        this.queryByState();
      }
    }
    else
    {
      this.filterService.getCachedTaskFilters(this.currentUser.userName).subscribe({
        next: (filterList) =>
        {
          const item = filterList.find(obj => obj.id.toString() === filter.toString());
          this.filterSelected = item;
          this.breadcrumb = item && item.name;
          this.stringLiteralBreadcrumb = true;
          this.state = JSON.parse(item.state);
          this.columnsConfig = item.columns ? JSON.parse(item.columns) : this.defaultColumnsConfig;

          // reset pagination numbers as we're more concerned with filters and sorting.
          this.state.skip = 0;
          this.queryByState();
        }
      });
    }
  }

  /**
   * Unselect any previous selected rows.
   */
  private unselectTask(): void
  {
    this.collapseAllRows();
  }

  /**
   * Collapse all task details opened.
   */
  private collapseAllRows()
  {
    this.expandedDetailKeys = [];
  }

  private isExpanded(id: string): boolean
  {
    let found = false;
    for (let i = 0; i < this.expandedDetailKeys.length; i++)
    {
      if (this.expandedDetailKeys[i] === id)
      {
        found = true;
        break;
      }
    }
    return found;
  }

  private autoFitColumns(): void
  {
    if (this.grid !== undefined && this.autoFitColumnsEnabled)
    {
      setTimeout(() =>
      {
        this.grid.autoFitColumns();
        this.grid.scrollTo({ row: 0, column: 0 });
      });
    }
  }

  private queryByState(): void
  {
    this.queryTasks(this.state);
  }

  private queryTasks(state: State): void
  {
    this.keysetPaging.runInitialQueryElse(state, (queryState) =>
    {
      this.taskListService.queryByState(queryState, null, '');
    });
  }

  private applyTaskListGridData(value: GridDataResult | null): void
  {
    const bound = this.keysetPaging.bindServiceGridRow(value);
    if (bound === null)
    {
      return;
    }
    if (value !== null)
    {
      TaskListUtils.finalizeDataset(value, this.taskUtilityService, this.eventListService);
    }
    this.gridData = bound.data;
    this.total = bound.total;
    this.gridView = { data: bound.data, total: bound.gridViewTotal };
    this.autoFitColumns();
  }
}
